(() => {
  const normalize = value => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const isIdentifier = value => /^[A-Za-z0-9.\/_-]+$/.test(value.trim()) && /\d/.test(value);
  const searchQuery = value => isIdentifier(value) ? normalize(value) : value.trim();
  const scopedModel = value => {
    const locked = window.XPartModelLock?.get?.();
    return locked?.model && locked?.url && !isIdentifier(value) ? locked : null;
  };
  const hubUrl = (model, query = '') => {
    const url = new URL(model.url, location.origin);
    if (url.origin !== location.origin || !url.pathname.startsWith('/pages/')) throw new Error('Invalid model URL');
    url.search = '';
    url.hash = '';
    if (query) url.searchParams.set('xp_search', query);
    return url;
  };
  const indexes = new Map();
  const modelIndex = async (model, signal) => {
    const url = hubUrl(model);
    const cached = indexes.get(url.pathname);
    if (cached && Date.now() - cached.time < 60000) return cached.doc;
    url.searchParams.set('section_id', 'xpart-model-search-index');
    const response = await fetch(url, {signal, credentials:'same-origin'});
    if (!response.ok) throw new Error('Model search unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-model-parts-index]');
    if (!doc || normalize(doc.dataset.model) !== normalize(model.model)) throw new Error('Model index mismatch');
    indexes.set(url.pathname, {doc, time:Date.now()});
    return doc;
  };
  window.XPartSearchPolicy = {isIdentifier, scopedModel, normalize, searchQuery};
  document.addEventListener('click', event => {
    const control = event.target.closest('[data-xpv-search-query], [data-xpv-assistant]');
    if (!control) return;
    const contactQuery = /\/pages\/contact\/?$/.test(location.pathname) ? new URL(location.href).searchParams.get('part_search') : '';
    const query = String(control.dataset.xpvSearchQuery || contactQuery || '').trim().slice(0, 200);
    if (!query) return;
    const assistant = document.querySelector('xpart-assistant-v3');
    const composer = assistant?.querySelector('[data-xpv-message]');
    if (typeof assistant?.open !== 'function' || !composer) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    assistant.open(control, true);
    const draft = control.dataset.xpvSearchMessage || query;
    if (!composer.value.includes(draft)) composer.value = composer.value.trim() ? `${composer.value.trim()}\n\n${draft}` : draft;
    composer.dispatchEvent(new Event('input', {bubbles: true}));
  }, {capture: true});
  const bind = form => {
    if (form.dataset.searchChoicesBound) return;
    form.dataset.searchChoicesBound = 'true';
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    const labels = JSON.parse(form.dataset.searchLabels);
    const panel = document.createElement('div');
    panel.className = 'xps-panel';
    panel.hidden = true;
    panel.id = `${input.id}-choices`;
    const list = document.createElement('div');
    list.id = `${panel.id}-list`;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', labels.choices);
    const all = document.createElement('a');
    all.className = 'xps-all';
    all.textContent = labels.all;
    const status = document.createElement('span');
    status.className = 'xpv-sr';
    status.setAttribute('role', 'status');
    const similar = document.createElement('button');
    similar.type = 'button';
    similar.className = 'xps-similar';
    similar.textContent = labels.similar;
    similar.hidden = true;
    similar.setAttribute('aria-expanded', 'false');
    similar.addEventListener('click', () => {
      const expanded = similar.getAttribute('aria-expanded') !== 'true';
      list.querySelectorAll('[data-similar-model]').forEach(item => item.hidden = !expanded);
      similar.setAttribute('aria-expanded', String(expanded));
      similar.textContent = expanded ? labels.hideSimilar : labels.similar;
      status.textContent = `${choices().length} ${labels.suggestions}`;
    });
    panel.append(list, similar, all, status);
    form.append(panel);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', list.id);
    input.setAttribute('aria-expanded', 'false');
    let timer, request, revision = 0, selected = -1, rendered = '';
    const choices = () => [...list.querySelectorAll('[data-search-choice]')].filter(item => !item.closest('[hidden]'));
    const numberChoices = () => list.querySelectorAll('[data-search-choice]').forEach((item,i) => item.id = `${list.id}-${i}`);
    const close = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      selected = -1;
    };
    const show = () => {
      if (document.activeElement !== input) return;
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const select = index => {
      const items = choices();
      if (!items.length) return;
      selected = (index + items.length) % items.length;
      items.forEach((item, i) => item.setAttribute('aria-selected', String(i === selected)));
      input.setAttribute('aria-activedescendant', items[selected].id);
      items[selected].scrollIntoView({block: 'nearest'});
    };
    const load = async () => {
      const query = input.value.trim();
      const current = ++revision;
      request?.abort();
      if (query.length < 3) { rendered = ''; close(); return; }
      request = new AbortController();
      const model = scopedModel(query);
      const full = model ? hubUrl(model,query) : new URL(form.action, location.origin);
      if (!model) {full.searchParams.set('q', query);full.searchParams.set('type', 'product,page');}
      all.textContent = model ? `${labels.allModel} ${model.model} →` : labels.all;
      similar.hidden = true;
      similar.setAttribute('aria-expanded','false');
      similar.textContent = labels.similar;
      all.href = full.pathname + full.search;
      list.replaceChildren();
      const message = document.createElement('p');
      message.className = 'xps-message';
      message.textContent = labels.loading;
      list.append(message);
      selected = -1;
      input.removeAttribute('aria-activedescendant');
      show();
      try {
        if (model) {
          const index = await modelIndex(model,request.signal);
          if (current !== revision || query !== input.value.trim()) return;
          const words = query.toLowerCase().split(/\s+/).filter(Boolean);
          const matches = [...index.querySelectorAll('[data-search-choice]')].filter(item => words.every(word => item.dataset.searchText.includes(word)));
          const group = document.createElement('div');
          group.setAttribute('role','group');
          group.setAttribute('aria-label',`${labels.forModel} ${model.model}`);
          const heading = document.createElement('h3');
          heading.textContent = `${labels.forModel} ${model.model}`;
          group.append(heading);
          matches.slice(0,8).forEach(item => group.append(item.cloneNode(true)));
          if (!matches.length) {
            const empty = document.createElement('p');
            empty.className = 'xps-message';
            empty.textContent = labels.noModelMatches;
            group.append(empty);
          }
          list.replaceChildren(group);
          numberChoices();
          rendered = query;
          status.textContent = `${matches.length} ${labels.suggestions}`;
          show();
          return;
        }
        const url = new URL(form.dataset.predictiveUrl, location.origin);
        url.search = new URLSearchParams({q: query, section_id: 'xpart-search-choices', 'resources[type]': 'page,product', 'resources[limit]': '5', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last'});
        const readResults = async searchUrl => {
          const response = await fetch(searchUrl, {signal: request.signal, credentials: 'same-origin'});
          if (!response.ok) throw new Error('Search failed');
          const html = await response.text();
          const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-xpart-search-choices]');
          if (!results) throw new Error('Missing results');
          return results;
        };
        let results = await readResults(url);
        const normalized = searchQuery(query);
        if (current !== revision || query !== input.value.trim()) return;
        if (results.querySelector('[data-xpart-search-empty]') && normalized !== query) {
          const retryUrl = new URL(url);
          retryUrl.searchParams.set('q', normalized);
          try {
            const retried = await readResults(retryUrl);
            if (!retried.querySelector('[data-xpart-search-empty]')) {
              results = retried;
              full.searchParams.set('q', normalized);
              all.href = full.pathname + full.search;
            }
          } catch (error) {
            if (error.name === 'AbortError') throw error;
          }
        }
        if (current !== revision || query !== input.value.trim()) return;
        list.replaceChildren(results);
        numberChoices();
        similar.hidden = !list.querySelector('[data-similar-model]');
        rendered = query;
        status.textContent = `${choices().length} ${labels.suggestions}`;
        show();
      } catch (error) {
        if (error.name === 'AbortError' || current !== revision) return;
        message.textContent = labels.fallback;
        status.textContent = labels.fallback;
      }
    };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      revision++;
      request?.abort();
      close();
      timer = setTimeout(load, 220);
    });
    input.addEventListener('focus', () => {
      if (rendered === input.value.trim() && rendered.length >= 3) show();
      else { clearTimeout(timer); timer = setTimeout(load, 100); }
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') { clearTimeout(timer); revision++; request?.abort(); close(); return; }
      if (panel.hidden) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        select(event.key === 'ArrowDown' ? selected + 1 : selected < 0 ? choices().length - 1 : selected - 1);
      } else if (event.key === 'Enter' && selected >= 0) {
        event.preventDefault();
        choices()[selected]?.click();
      }
    });
    form.addEventListener('focusout', () => setTimeout(() => { if (!form.contains(document.activeElement)) close(); }, 0));
    document.addEventListener('pointerdown', event => { if (!form.contains(event.target)) close(); });
    list.addEventListener('click', event => {
      const link = event.target.closest('[data-search-hub]');
      if (!link) return;
      const url = new URL(link.href,location.origin);
      const model = {model:link.dataset.model,brand:link.dataset.brand,appliance:link.dataset.appliance,url:url.pathname};
      if (window.XPartModelLock?.set) window.XPartModelLock.set(model);
      else {try {localStorage.setItem('xpart_locked_model_v1',JSON.stringify(model));} catch {} window.dispatchEvent(new CustomEvent('xpart:model-changed',{detail:model}));}
    });
    form.addEventListener('submit', event => {
      clearTimeout(timer);
      request?.abort();
      const model = scopedModel(input.value.trim());
      if (model) {event.preventDefault();location.assign(hubUrl(model,input.value.trim()).href);}
      close();
    });
    window.addEventListener('xpart:model-changed', () => {indexes.clear();rendered='';request?.abort();revision++;close();});
  };
  const init = () => document.querySelectorAll('form[data-xpm-search][data-search-labels]').forEach(bind);
  init();
  document.addEventListener('shopify:section:load', init);
})();

